import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { Caption, COLORS, Panel, Badge } from './ui'

// NEW chapter — the missing keystone: how a model actually learns.
// Forward pass → loss (prediction vs label) → backprop → update, on repeat.
const STEPS = [
  { t: '①', label: 'Forward pass', desc: 'image → prediction', color: COLORS.indigo },
  { t: '②', label: 'Compare to label', desc: 'loss = how wrong', color: COLORS.pink },
  { t: '③', label: 'Backpropagation', desc: 'blame each weight', color: COLORS.purple },
  { t: '④', label: 'Update weights', desc: 'nudge to reduce loss', color: COLORS.green },
]

export function Learning2D() {
  const [mobile, setMobile] = useState(() => window.innerWidth < 768)
  useEffect(() => {
    const handler = () => setMobile(window.innerWidth < 768)
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }, [])

  return (
    <div style={{
      position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', overflowY: 'auto',
      alignItems: 'center', justifyContent: 'center', gap: 18, padding: mobile ? '80px 20px 90px' : '90px 24px 110px',
      fontFamily: "'General Sans', system-ui, sans-serif", textAlign: 'center', color: COLORS.text,
    }}>
      <Badge color={COLORS.amber}>The Training Loop</Badge>

      <div style={{ display: 'flex', flexDirection: mobile ? 'column' : 'row', alignItems: 'center', justifyContent: 'center', gap: mobile ? 14 : 30, flexWrap: 'wrap', width: mobile ? '100%' : undefined }}>
        {/* The cycle */}
        <div style={{ display: 'flex', flexDirection: mobile ? 'column' : 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'center', maxWidth: mobile ? 320 : 560, width: mobile ? '100%' : undefined }}>
          {STEPS.map((s, i) => (
            <div key={i} style={{ display: 'flex', flexDirection: mobile ? 'column' : 'row', alignItems: 'center', gap: 8, width: mobile ? '100%' : undefined }}>
              <motion.div
                animate={{ boxShadow: [`0 0 0px ${s.color}00`, `0 0 22px ${s.color}88`, `0 0 0px ${s.color}00`] }}
                transition={{ duration: 3.2, repeat: Infinity, delay: i * 0.8 }}
                style={{ width: mobile ? '100%' : undefined }}
              >
                <Panel glow={s.color} style={{ width: mobile ? '100%' : 124, padding: 12 }}>
                  <div style={{ fontSize: 20, color: s.color }}>{s.t}</div>
                  <div style={{ fontSize: 14, fontWeight: 700, marginTop: 4 }}>{s.label}</div>
                  <div style={{ fontSize: 12.5, color: COLORS.textDim, marginTop: 2 }}>{s.desc}</div>
                </Panel>
              </motion.div>
              <span style={{ color: COLORS.textFaint, fontSize: 16, transform: mobile ? 'rotate(90deg)' : 'none' }}>{i < STEPS.length - 1 ? '→' : '↺'}</span>
            </div>
          ))}
        </div>

        {/* Loss curve */}
        <Panel style={{ width: mobile ? '100%' : 230 }}>
          <div style={{ fontSize: 12.5, fontFamily: "'JetBrains Mono', ui-monospace, monospace", color: COLORS.textDim, marginBottom: 8, textAlign: 'left' }}>
            loss ↓ over epochs
          </div>
          <svg viewBox="0 0 200 110" style={{ width: '100%' }}>
            <polyline points="0,8 20,8 40,8 200,8" fill="none" stroke="rgba(255,255,255,0.08)" strokeDasharray="3 3" />
            <motion.path
              d="M4,12 C40,20 60,70 100,86 S160,102 196,104"
              fill="none" stroke={COLORS.amber} strokeWidth="2.5" strokeLinecap="round"
              initial={{ pathLength: 0 }} animate={{ pathLength: 1 }}
              transition={{ duration: 2.4, repeat: Infinity, repeatType: 'reverse' }}
            />
            <text x="6" y="22" fill={COLORS.textFaint} fontSize="9" fontFamily="monospace">high</text>
            <text x="150" y="100" fill={COLORS.textFaint} fontSize="9" fontFamily="monospace">low</text>
          </svg>
        </Panel>
      </div>

      <Caption>
        Models aren’t programmed — they’re <strong>trained</strong>. Each example produces a prediction; a
        <em> loss</em> measures how wrong it is; backpropagation traces that error to every weight; gradient
        descent nudges them. Repeat over millions of examples and the weights converge.
      </Caption>
    </div>
  )
}
